import React, { useState, useRef, useEffect } from 'react';
import analyticsEvent from 'services/analytic/analytic-service';
import getBrandLogoUrl from 'utilities/brandLogo/getBrandLogoUrl';
import { DeliveryAndReturnContentItem, DeliveryAndReturnContentKey } from 'types/Product';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import { VDPService } from 'services/VDP/VDP-service';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { startSpan, endSpan, failSpan } from 'utilities/loggerv2/performanceSpans';
import vdpDeliveryContent from 'utilities/content/vdpDeliveryContent';
import { DeliveryAndReturnsProps } from './DeliveryAndReturnsProps';
import DeliveryAndReturnsModal from './Dialog/DeliveryAndReturnsDialog';
import DeliveryCard from './Card/DeliveryCard';
import Skeleton from './Skeleton/Skeleton';
import ErrorCodeDefinition from '../../utilities/loggerv2/errorCodeDefinition';

const loggerVDP = LoggerFactory.getLogger('DeliveryAndReturnsVDP');

const DEFAULT_CONTENT: DeliveryAndReturnContentItem = {
  pageText: 'Please select an option to view delivery options on this item',
  pageLink: '',
  modalHeading: '',
  modalBody: '',
  title: 'Delivery and returns',
};

const CLICK_AND_COLLECT = 'Click and collect';
const ONE_PERSON = 'OnePerson';

function DeliveryAndReturns(props: DeliveryAndReturnsProps) {
  const { brand, skus, csrfToken, vdpMockEnabled, mockResponseName, site } = props;
  const { selectedSku } = useSelectedSkuContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRendered, setDialogRendered] = useState(false);
  const isFirstRender = useRef(true);
  const selectedContentKey = selectedSku?.deliveryAndReturnContentKey;
  const [vdpLoading, setVDPLoading] = useState(true);
  const [VDP, setVDP] = useState<any>();
  const [deliveryContentMap, setDeliveryContentMap] = useState<Record<string, DeliveryAndReturnContentItem>>({});

  const vdpService = new VDPService(vdpMockEnabled);

  const brandLogoUrl =
    brand && selectedContentKey === DeliveryAndReturnContentKey.FFM ? getBrandLogoUrl(brand) : undefined;

  const sendAnalyticsEvent = (action: 'Open' | 'Closed') => {
    if (selectedContentKey) {
      analyticsEvent({
        gaLabel: `Delivery and Returns - ${
          selectedSku ? deliveryContentMap[selectedSku?.option ?? '']?.pageText : DEFAULT_CONTENT.pageText
        } - ${selectedSku ? deliveryContentMap[selectedSku?.option ?? '']?.pageLink : DEFAULT_CONTENT.pageLink} - ${action}`,
      });
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    sendAnalyticsEvent('Closed');
  };

  const openDialog = () => {
    if (dialogRendered) {
      setDialogOpen(true);
    } else {
      setDialogRendered(true);
    }

    sendAnalyticsEvent('Open');
  };

  const getVDPRequestSkus = () => {
    const requestSkus = skus.map((sku) => ({
      sku: sku.option,
      productTypeCode: sku.productTypeCode || 1,
    }));

    return requestSkus;
  };

  const logAndFailSpan = async (spanId: string, message: string, code: ErrorCodeDefinition, stack = '') => {
    const error = new Error(message);
    Object.defineProperty(error, 'code', { value: code, writable: true, enumerable: true });

    (await loggerVDP).logWithCode(code, error);

    failSpan(spanId, {
      errorType: code,
      message,
      stack,
    });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (dialogRendered) {
      setDialogOpen(true);
    }
  }, [dialogRendered]);

  useEffect(() => {
    if (!vdpLoading && VDP && selectedSku?.option && !(selectedSku.option in deliveryContentMap)) {
      const vdpContent = vdpDeliveryContent(selectedContentKey, selectedSku.option, VDP, site, brand);

      if (vdpContent && typeof vdpContent === 'object') {
        setDeliveryContentMap((prev) => ({
          ...prev,
          [selectedSku.option]: vdpContent as DeliveryAndReturnContentItem,
        }));
      }
    }
  }, [vdpLoading, VDP, selectedSku, deliveryContentMap, selectedContentKey]);

  useEffect(() => {
    if (!csrfToken && !vdpMockEnabled) return;

    const getVDP = async () => {
      const skusPayload = getVDPRequestSkus();

      const spanId = startSpan('getVDP', {
        skus: skusPayload,
        csrfTokenProvided: true,
        vdpMockEnabled,
      });

      try {
        const response = await vdpService.getVDP({ skus: skusPayload }, csrfToken, {
          vdpMockEnabled,
          mockResponseName,
          site: site || 'very',
        });

        if (!response) {
          await logAndFailSpan(spanId, 'VDP API returned an empty response.', ErrorCodes.API_INVALID_RESPONSE);
          return;
        }

        setVDP(response);
        endSpan(spanId, { success: true });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        await logAndFailSpan(spanId, message, ErrorCodes.API_REQUEST_FAILED, error?.stack);
      } finally {
        setVDPLoading(false);
      }
    };

    getVDP();
  }, [csrfToken]);

  if (vdpLoading) {
    return <Skeleton />;
  }

  if (!VDP || (selectedSku && !selectedSku.deliveryAndReturnContentKey)) return null;
  return (
    <>
      <DeliveryCard
        onClick={openDialog}
        text={selectedSku ? deliveryContentMap[selectedSku?.option ?? '']?.pageText : DEFAULT_CONTENT.pageText}
        link={selectedSku ? deliveryContentMap[selectedSku?.option ?? '']?.pageLink : DEFAULT_CONTENT.pageLink}
        title={selectedSku?.deliveryAndReturnContentKey === ONE_PERSON ? CLICK_AND_COLLECT : DEFAULT_CONTENT.title}
      />
      {dialogRendered && (
        <DeliveryAndReturnsModal
          body={deliveryContentMap[selectedSku?.option ?? '']?.modalBody}
          brand={brand}
          brandLogoUrl={brandLogoUrl}
          heading={deliveryContentMap[selectedSku?.option ?? '']?.modalHeading}
          onClose={closeDialog}
          open={dialogOpen}
        />
      )}
    </>
  );
}

export default DeliveryAndReturns;
